(function () {
'use strict';

if (window.PartVendBuyNowCustomLoaded) return;
window.PartVendBuyNowCustomLoaded = true;

const PARTVEND_APP_URL = 'https://user.partvend.com';
const CHECKOUT_SESSION_ENDPOINT = `${PARTVEND_APP_URL}/api/storefront/checkout-sessions`;

// Firefox can restore this page from its back/forward cache with the DOM in
// the exact state it had during navigation. Clear any checkout loader when
// the page is shown again so Buy It Now remains usable after pressing Back.
window.addEventListener('pageshow', resetLoadingBuyNowButtons);

function getPartVendBuyerLoginUrl() {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  const loginUrl = new URL('/login', PARTVEND_APP_URL);
  loginUrl.searchParams.set('role', 'buyer');
  loginUrl.searchParams.set('return_to', returnTo || '/');
  return loginUrl.href;
}

document.addEventListener('click', async function (event) {
  const button = event.target.closest('.custom-buy-now-button');
  if (!button) return;

  event.preventDefault();
  if (button.dataset.loading === 'true') return;

  const customerSession = getPartVendCustomerSession();
  if (!customerSession) {
    showLoginModal(getPartVendBuyerLoginUrl());
    return;
  }

  setButtonLoading(button, true);

  try {
    const root = window.Shopify?.routes?.root || '/';
    const isCartCheckout = button.dataset.cartCheckout === 'true';
    let selectedPurchase = null;

    if (!isCartCheckout) {
      const productForm =
        button.closest('form[action*="/cart/add"]') ||
        button.closest('product-form')?.querySelector('form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]');

      if (!productForm) throw new Error('Product form not found.');

      const quantity = resolveSelectedQuantity(productForm, button);
      const variantId = await resolveVariantId(productForm, button);

      if (!variantId) throw new Error('No valid variant found for this product.');
      const added = await addProductToCart(root, variantId, quantity);
      selectedPurchase = {
        variantId: String(variantId),
        quantity,
        item: Array.isArray(added?.items) ? added.items[0] : added,
      };
    }

    const cart = await getCurrentCart(root);
    if (!cart?.items?.length) throw new Error('Your cart is empty.');
    const checkoutItems = selectedPurchase
      ? [buildSelectedCheckoutItem(cart, selectedPurchase)]
      : cart.items;

    const sessionResponse = await fetch(CHECKOUT_SESSION_ENDPOINT, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        customer: {
          shopify_customer_id: customerSession.customerId,
          session_token: customerSession.sessionToken,
        },
        shop: window.Shopify?.shop,
        cart_token: cart.token,
        cart_total_price: checkoutItems.reduce(
          (total, item) => total + Number(
            item.line_price ?? (Number(item.price ?? 0) * Number(item.quantity ?? 0))
          ),
          0
        ),
        cart_currency: cart.currency,
        cart_items: checkoutItems.map((item) => ({
          key: item.key,
          variant_id: item.variant_id,
          product_id: item.product_id,
          quantity: item.quantity,
          title: item.product_title,
          variant_title: item.variant_title,
          vendor: item.vendor,
          sku: item.sku,
          image: item.image,
          url: item.url,
          price: item.price,
          line_price: item.line_price,
          properties: item.properties,
        })),
      }),
    });

    const sessionData = await safeJson(sessionResponse);

    if (sessionResponse.status === 401) {
      clearPartVendCustomerSession();
      showLoginModal(getPartVendBuyerLoginUrl());
      throw new Error(sessionData?.message || 'Your session expired. Please log in again.');
    }

    if (!sessionResponse.ok || !sessionData?.success) {
      throw new Error(buildCheckoutErrorMessage(sessionData));
    }

    const redirectUrl = sessionData?.data?.redirect_url;
    if (!redirectUrl) throw new Error('Missing checkout redirect URL.');

    window.location.href = redirectUrl;
  } catch (error) {
    console.error(error);
    alert(error.message || 'Something went wrong.');
    setButtonLoading(button, false);
  }
});

function getPartVendCustomerSession() {
  const customerId = localStorage.getItem('partvend_customer_id');
  const sessionToken = localStorage.getItem('partvend_session_token');
  const expiresAt = Number(localStorage.getItem('partvend_session_expires_at') || 0);

  if (!customerId || !sessionToken || expiresAt <= Date.now()) {
    clearPartVendCustomerSession();
    return null;
  }

  return { customerId, sessionToken, expiresAt };
}

function clearPartVendCustomerSession() {
  localStorage.removeItem('partvend_customer_id');
  localStorage.removeItem('partvend_session_token');
  localStorage.removeItem('partvend_session_expires_at');
}

async function addProductToCart(root, variantId, quantity) {
  const response = await fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: String(variantId), quantity }] }),
  });
  const data = await safeJson(response);
  if (!response.ok) throw new Error(data?.description || data?.message || 'Unable to add this product to cart.');
  return data;
}

async function getCurrentCart(root) {
  const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
  const cart = await safeJson(response);
  if (!response.ok) throw new Error('Unable to read cart. Please refresh the page and try again.');
  return cart;
}

function resolveSelectedQuantity(productForm, button) {
  // HTMLFormElement.elements includes controls linked with form="..." even
  // when the quantity widget is rendered elsewhere in the product section.
  const associatedControl = typeof productForm.elements?.namedItem === 'function'
    ? productForm.elements.namedItem('quantity')
    : null;
  const sectionQuantityInput = button.closest('[id^="shopify-section-"]')
    ?.querySelector('[name="quantity"]');
  const quantityInput = associatedControl?.value !== undefined
    ? associatedControl
    : productForm.querySelector('[name="quantity"]') || sectionQuantityInput;
  const requestedQuantity = Number.parseInt(quantityInput?.value ?? '1', 10);

  return Number.isSafeInteger(requestedQuantity) && requestedQuantity > 0
    ? requestedQuantity
    : 1;
}

function buildSelectedCheckoutItem(cart, selectedPurchase) {
  const addedKey = selectedPurchase.item?.key;
  const cartItem = (cart.items || []).find(
    (item) => addedKey && String(item.key) === String(addedKey)
  ) || (cart.items || []).find(
    (item) => String(item.variant_id) === selectedPurchase.variantId
  ) || selectedPurchase.item;

  if (!cartItem) throw new Error('The selected product was not found in your cart.');

  const unitPrice = Number(cartItem.final_price ?? cartItem.price ?? 0);

  return {
    ...cartItem,
    quantity: selectedPurchase.quantity,
    line_price: unitPrice * selectedPurchase.quantity,
  };
}

function buildCheckoutErrorMessage(data) {
  if (data?.code === 'CART_STOCK_ERROR' && Array.isArray(data.items)) {
    const messages = data.items.map((item) => Number(item.available_quantity ?? 0) <= 0
      ? `${item.title || 'This product'} is out of stock. Please remove it from your cart.`
      : `${item.title || 'This product'} has only ${item.available_quantity} available, but you requested ${item.requested_quantity ?? item.quantity ?? '-'}.`
    ).join('\n');
    return `Some products in your cart cannot be checked out:\n\n${messages}\n\nPlease update your cart.`;
  }
  return data?.message || 'Unable to create checkout session.';
}

async function safeJson(response) {
  try { return await response.json(); } catch { return null; }
}

function setButtonLoading(button, loading) {
  if (loading) {
    button.dataset.loading = 'true';
    button.dataset.originalHtml = button.innerHTML;
    button.dataset.originalDisabled = button.disabled ? 'true' : 'false';
    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    button.innerHTML = `<span class="custom-checkout-label">${button.dataset.originalHtml}</span><span class="custom-checkout-spinner" aria-hidden="true"></span>`;
    return;
  }
  button.dataset.loading = 'false';
  if (button.dataset.originalHtml) button.innerHTML = button.dataset.originalHtml;
  button.disabled = button.dataset.originalDisabled === 'true';
  button.classList.remove('is-loading');
  button.removeAttribute('aria-busy');
  delete button.dataset.originalHtml;
  delete button.dataset.originalDisabled;
}

function resetLoadingBuyNowButtons() {
  document.querySelectorAll(
    '.custom-buy-now-button[data-loading="true"], ' +
    '.custom-buy-now-button.is-loading, ' +
    '.custom-buy-now-button[aria-busy="true"]'
  ).forEach((button) => setButtonLoading(button, false));
}

function showLoginModal(loginUrl) {
  const modal = document.getElementById('custom-login-modal');
  if (!modal) {
    window.location.href = loginUrl;
    return;
  }

  const actions = modal.querySelector('.custom-login-modal__actions');
  if (!actions) {
    window.location.href = loginUrl;
    return;
  }

  let loginLink = actions.querySelector('[data-partvend-modal-buyer-login]')
    || actions.querySelector('a.button:not([data-login-modal-close])');

  if (!loginLink) {
    loginLink = document.createElement('a');
    loginLink.className = 'button button--secondary';
    actions.appendChild(loginLink);
  }

  actions.querySelectorAll('a.button').forEach((link) => {
    link.hidden = link !== loginLink;
  });

  loginLink.hidden = false;
  loginLink.removeAttribute('data-login-modal-close');
  loginLink.setAttribute('data-partvend-modal-buyer-login', '1');
  loginLink.setAttribute('data-partvend-buyer-link', '1');
  loginLink.href = loginUrl;
  loginLink.textContent = 'Buyer Login';
  modal.hidden = false;
  modal.classList.add('is-open');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('custom-login-modal-open');
}

function closeLoginModal() {
  const modal = document.getElementById('custom-login-modal');
  if (!modal) return;
  modal.hidden = true;
  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('custom-login-modal-open');
}

window.closeLoginModal = closeLoginModal;

document.addEventListener('click', function (event) {
  const closeTrigger = event.target.closest('[data-login-modal-close]');
  if (closeTrigger) {
    event.preventDefault();
    event.stopPropagation();
    closeLoginModal();
    return;
  }
  const modal = document.getElementById('custom-login-modal');
  if (!modal || modal.hidden) return;
  const box = modal.querySelector('.custom-login-modal__box');
  if (modal.contains(event.target) && box && !box.contains(event.target)) closeLoginModal();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeLoginModal();
});

async function resolveVariantId(productForm, button) {
  const root = window.Shopify?.routes?.root || '/';
  const handle = button.dataset.productHandle || getProductHandle();
  if (!handle) return getHiddenVariantId(productForm) || button.dataset.fallbackVariantId || null;

  const response = await fetch(`${root}products/${handle}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) return getHiddenVariantId(productForm) || button.dataset.fallbackVariantId || null;

  const product = await response.json();
  const variants = product?.variants || [];
  const hiddenId = getHiddenVariantId(productForm);
  const hiddenVariant = variants.find((variant) => String(variant.id) === String(hiddenId));
  if (hiddenVariant) {
    if (!hiddenVariant.available) throw new Error('This product is sold out.');
    return String(hiddenVariant.id);
  }

  const fallbackId = button.dataset.fallbackVariantId;
  const fallbackVariant = variants.find((variant) => String(variant.id) === String(fallbackId));
  if (fallbackVariant) {
    if (!fallbackVariant.available) throw new Error('This product is sold out.');
    return String(fallbackVariant.id);
  }
  return String(variants.find((variant) => variant.available)?.id || '') || null;
}

function getHiddenVariantId(productForm) {
  return extractNumericId(String(productForm.querySelector('[name="id"]')?.value || '').trim());
}

function getProductHandle() {
  return window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1] || null;
}

function extractNumericId(value) {
  return String(value || '').match(/(\d+)$/)?.[1] || null;
}

})();
